import {
	CHECKERS_SIZE,
	type CheckersState,
	type GoFishState,
	movesForPiece,
	OLD_MAID,
	type OldMaidState,
	type PlayerIndex,
	rankLabel,
} from "@playpal/core";
import { useState } from "react";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

// --- Go Fish --------------------------------------------------------------

export function GoFishBoard({ state, seat, onMove }: BoardProps<GoFishState>) {
	const myTurn = state.turn === seat && !state.result;
	const them: PlayerIndex = seat === 0 ? 1 : 0;
	const asks = [...new Set(state.hands[seat])].sort((a, b) => a - b);

	return (
		<div className="board-frame board-lg text-center">
			<p className="mb-2 text-lg font-semibold">
				Your sets: {state.books[seat].length} · theirs: {state.books[them].length}
			</p>
			<p className="mb-4 text-sm opacity-70">
				They hold {state.counts[them]} cards · {state.poolLeft} left in the pool
			</p>

			<p className="mb-2 font-bold">Your cards — tap one to ask for it</p>
			<div className="mb-4 flex flex-wrap justify-center gap-2">
				{asks.map((rank) => {
					const held = state.hands[seat].filter((card) => card === rank).length;
					return (
						<button
							key={rank}
							type="button"
							disabled={!myTurn}
							aria-label={`Ask for ${rankLabel(rank)}`}
							onClick={() => onMove({ rank })}
							className="flex h-20 w-14 flex-col items-center justify-center rounded-xl bg-white text-indigo-950 shadow-md transition enabled:hover:-translate-y-1 disabled:opacity-60"
						>
							<span className="text-2xl font-black">{rankLabel(rank)}</span>
							{held > 1 && <span className="text-xs font-bold">× {held}</span>}
						</button>
					);
				})}
			</div>

			{state.books[seat].length > 0 && (
				<p className="mb-2 text-sm font-semibold">
					Your sets: {state.books[seat].map(rankLabel).join(" · ")}
				</p>
			)}
			<p className="h-6 text-lg font-semibold" aria-live="polite">
				{state.lastEvent ?? (myTurn ? "Ask for a card!" : "They're thinking…")}
			</p>
		</div>
	);
}

// --- Old Maid -------------------------------------------------------------

export function OldMaidBoard({ state, seat, onMove }: BoardProps<OldMaidState>) {
	const myTurn = state.turn === seat && !state.result;
	const them: PlayerIndex = seat === 0 ? 1 : 0;

	return (
		<div className="board-frame board-lg text-center">
			<p className="mb-4 text-lg font-semibold">
				Pairs thrown away — you {state.discarded[seat]}, them {state.discarded[them]}
			</p>

			<p className="mb-2 font-bold">Their cards — pick one, any one</p>
			<div className="mb-6 flex flex-wrap justify-center gap-2">
				{state.hands[them].map((_, index) => (
					<button
						key={index}
						type="button"
						disabled={!myTurn}
						aria-label={`Take their card ${index + 1}`}
						onClick={() => onMove({ index })}
						className="h-20 w-14 rounded-xl bg-indigo-500 text-3xl text-indigo-200 shadow-md transition enabled:hover:-translate-y-1 disabled:opacity-60"
					>
						?
					</button>
				))}
			</div>

			<p className="mb-2 font-bold">Your cards</p>
			<div className="flex flex-wrap justify-center gap-2">
				{state.hands[seat].map((card, index) => (
					<span
						key={`${card}-${index}`}
						className={`flex h-20 w-14 items-center justify-center rounded-xl text-2xl font-black shadow-md ${
							card === OLD_MAID ? "bg-rose-400 text-rose-950" : "bg-white text-indigo-950"
						}`}
					>
						{card === OLD_MAID ? "🃏" : rankLabel(card)}
					</span>
				))}
			</div>

			<p className="mt-4 h-6 text-lg font-semibold" aria-live="polite">
				{state.lastEvent ?? (myTurn ? "Take one of their cards" : "They're choosing…")}
			</p>
		</div>
	);
}

// --- Checkers -------------------------------------------------------------

export function CheckersBoard({ state, seat, onMove }: BoardProps<CheckersState>) {
	const myTurn = state.turn === seat && !state.result;
	const [picked, setPicked] = useState<number | null>(null);

	const selected = state.mustJumpFrom ?? picked;
	const targets = selected !== null ? movesForPiece(state, selected).map((move) => move.to) : [];

	return (
		<div className="board-frame board-lg">
			<p className="mb-2 text-center text-lg font-semibold">
				Pieces — you {state.scores[seat]}, them {state.scores[seat === 0 ? 1 : 0]}
				{state.mustJumpFrom !== null && " · keep jumping!"}
			</p>

			<div
				className="grid overflow-hidden rounded-2xl shadow-xl"
				style={{ gridTemplateColumns: `repeat(${CHECKERS_SIZE}, minmax(0, 1fr))` }}
			>
				{state.board.map((cell, square) => {
					const dark = (Math.floor(square / CHECKERS_SIZE) + square) % 2 === 1;
					const mine = cell?.owner === seat;
					const isTarget = targets.includes(square);

					return (
						<button
							key={square}
							type="button"
							disabled={!myTurn || (!isTarget && !mine)}
							aria-label={`Row ${Math.floor(square / CHECKERS_SIZE) + 1}, column ${
								(square % CHECKERS_SIZE) + 1
							}${cell ? (cell.king ? ", king" : ", piece") : ", empty"}`}
							onClick={() => {
								if (isTarget && selected !== null) {
									onMove({ from: selected, to: square });
									setPicked(null);
								} else if (mine) {
									setPicked(square === picked ? null : square);
								}
							}}
							className={`flex aspect-square items-center justify-center ${
								dark ? "bg-amber-800" : "bg-amber-100"
							} ${isTarget ? "ring-4 ring-inset ring-emerald-400" : ""} ${
								square === selected ? "ring-4 ring-inset ring-indigo-400" : ""
							}`}
						>
							{cell && (
								<span
									className={`flex h-[78%] w-[78%] items-center justify-center rounded-full text-lg shadow-md ${
										cell.owner === 0 ? "bg-rose-500" : "bg-sky-500"
									}`}
								>
									{cell.king ? "👑" : ""}
								</span>
							)}
						</button>
					);
				})}
			</div>

			<p className="mt-3 text-center text-sm opacity-70">
				Tap one of your pieces, then a green square. You are {seat === 0 ? "red" : "blue"}.
			</p>
		</div>
	);
}
